import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type AvatarSize = 's' | 'm' | 'l' | 'xl';
/** 1..6 → the muted avatar palette `--av-1`..`--av-6`. `null` → `--av-default`. */
export type AvatarColor = 1 | 2 | 3 | 4 | 5 | 6 | null;

/**
 * `<nb-avatar>` — the design-system avatar (Mockup `.av` + sizes/colours/dots).
 *
 * Renders an image when `src` is supplied (object-fit: cover), otherwise the
 * `initials` on a muted colour fill. Optional corner dots flag a new (accent)
 * or online (success) status. The whole element is exposed to assistive tech as
 * a single `role="img"` whose accessible name pairs any status with text, so the
 * status is never conveyed by colour alone.
 *
 * @example
 * <nb-avatar initials="MA" [color]="3" size="l" online />
 * <nb-avatar src="/u/42.jpg" alt="Marit Aas" round newDot />
 */
@Component({
  selector: 'nb-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'img',
    '[attr.aria-label]': 'ariaLabel()',
    '[class]': 'classes()',
  },
  template: `
    @if (src(); as source) {
      <img [src]="source" [alt]="''" />
    } @else {
      <span class="initials" aria-hidden="true">{{ initials() }}</span>
    }
    @if (newDot()) {
      <span class="newdot" aria-hidden="true"></span>
    }
    @if (online()) {
      <span class="ondot" aria-hidden="true"></span>
    }
  `,
  styleUrl: './avatar.css',
})
export class NbAvatar {
  /** Pixel preset: s 36 · m 46 · l 58 · xl 88. */
  readonly size = input<AvatarSize>('m');
  /** Fully circular instead of the squircle radius. */
  readonly round = input(false);
  /** Palette index 1..6 (→ `--av-1`..`--av-6`); omit for `--av-default`. */
  readonly color = input<AvatarColor>(null);
  /** Initials shown when no `src` is provided. */
  readonly initials = input('');
  /** Image URL; when set an `<img>` is rendered instead of initials. */
  readonly src = input<string>();
  /** Accessible name for the person/entity (used for the image alt + label). */
  readonly alt = input('');
  /** Accent corner dot — flags something new. */
  readonly newDot = input(false);
  /** Success corner dot — flags an online status. */
  readonly online = input(false);

  protected readonly classes = computed(() => {
    const c: string[] = ['av', this.size()];
    if (this.round()) c.push('round');
    const color = this.color();
    if (color != null) c.push('c' + color);
    return c.join(' ');
  });

  protected readonly ariaLabel = computed(() => {
    const base = this.alt() || this.initials();
    const status: string[] = [];
    if (this.online()) status.push('online');
    if (this.newDot()) status.push('new');
    const suffix = status.length ? ` (${status.join(', ')})` : '';
    return (base + suffix).trim() || null;
  });
}
